import React, { useState, useEffect, useCallback, useRef } from "react";
import showData from "../../Utils/APIs/getMethod";
import { IoIosSave } from "react-icons/io";
import { downloadReport } from "../../Utils/APIs/downloadExcelAPI";

const EnquiryList = () => {
  // ---------- State ----------
  // Applied filters - trigger API calls when changed
  const [appliedFilters, setAppliedFilters] = useState({
    page: 1,
    limit: 10,
    search: "",
    customer_type: "",
    name: "",
    contact_number: "",
    state: "",
    isInterested: "",
    status: "",
    created_byId: "",
  });

  // Temporary filters - bound to UI inputs, only applied on button click
  const [tempFilters, setTempFilters] = useState({
    search: "",
    customer_type: "",
    name: "",
    contact_number: "",
    state: "",
    isInterested: "",
    status: "",
    created_byId: "",
  });

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [employees, setEmployees] = useState([]); // For employee filter dropdown

  // Ref to prevent multiple identical API calls on mount
  const isMounted = useRef(true);

  // ---------- Build query string from applied filters and sort ----------
  const buildQueryParams = useCallback(() => {
    const params = {};
    // Add only non‑empty filters from appliedFilters
    Object.keys(appliedFilters).forEach((key) => {
      const val = appliedFilters[key];
      if (val !== "" && val !== null && val !== undefined) {
        params[key] = val;
      }
    });
    // Add sorting parameters
    if (sortConfig.key) {
      params.sortBy = sortConfig.key;
      params.sortOrder = sortConfig.direction;
    }
    return params;
  }, [appliedFilters, sortConfig]);

  // ---------- Fetch data from API ----------
  const fetchEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      const params = buildQueryParams();
      // Assuming showData accepts (endpoint, queryParams)
      const response = await showData("external/enquiries", params);

      // Normalize response – supports common API shapes
      let enquiries = [];
      let total = 0;
      if (Array.isArray(response)) {
        enquiries = response;
        total = response.length;
      } else if (response?.data && Array.isArray(response.data)) {
        enquiries = response.data;
        total = response.total ?? response.data.length;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        enquiries = response.data.data;
        total = response.data.total ?? enquiries.length;
      } else if (response?.enquiries && Array.isArray(response.enquiries)) {
        enquiries = response.enquiries;
        total = response.total ?? enquiries.length;
      } else {
        enquiries = [];
        total = 0;
      }

      if (isMounted.current) {
        setData(enquiries);
        setTotalRecords(total);
        setError(null);
      }
    } catch (err) {
      console.error("API Error:", err);
      if (isMounted.current) {
        setError("Failed to load enquiries. Please try again later.");
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [buildQueryParams]);

  // Fetch when applied filters or sorting change
  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  // ---------- Filter handlers ----------
  // Only updates temporary filters (UI state)
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Apply temporary filters to appliedFilters and reset to page 1
  const handleApplyFilters = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      ...tempFilters,
      page: 1, // Reset to first page when applying new filters
    }));
  };

  // Clear both temporary and applied filters, reset sort
  const clearFilters = () => {
    const defaultFilters = {
      page: 1,
      limit: 10,
      search: "",
      customer_type: "",
      name: "",
      contact_number: "",
      state: "",
      isInterested: "",
      status: "",
      created_byId: "",
    };
    const defaultTemp = {
      search: "",
      customer_type: "",
      name: "",
      contact_number: "",
      state: "",
      isInterested: "",
      status: "",
      created_byId: "",
    };
    setAppliedFilters(defaultFilters);
    setTempFilters(defaultTemp);
    setSortConfig({ key: "createdAt", direction: "desc" });
  };

  // ---------- Sorting ----------
  const requestSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    // Reset page to 1 when sorting changes
    setAppliedFilters((prev) => ({ ...prev, page: 1 }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "↕️";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // Pagination controls
  const totalPages = Math.ceil(totalRecords / appliedFilters.limit);
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setAppliedFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Helper: Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid date";
    }
  };

  // Helper: Format coordinates
  const formatCoordinates = (locationArray) => {
    if (!locationArray || locationArray.length < 2) return "N/A";
    return `${locationArray[0].toFixed(4)}°, ${locationArray[1].toFixed(4)}°`;
  };

  // fetch employee list for filter dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await showData("external/employee");
        // Process employee data if needed
        setEmployees(response.data || []);
      } catch (err) {
        console.error("Failed to fetch employees:", err);
      }
    };
    fetchEmployees();
  }, []);
  function handleDownloadCSV() {
    downloadReport("/external/getEnquiriesInTheExcel");
  }

  // Loading state (only show full‑screen spinner on initial load)
  if (loading && data.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-yellow-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading enquiries...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-yellow-50">
        <div className="text-center max-w-md px-4">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => fetchEnquiries()}
            className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 bg-gradient-to-br from-white via-yellow-50 to-white min-h-screen font-sans">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-3xl font-bold text-gray-900 border-l-4 border-yellow-500 pl-4">
            Enquiry Management
          </h1>
          <button
            onClick={clearFilters}
            className="text-sm text-yellow-600 hover:text-yellow-800 underline"
          >
            Clear all filters
          </button>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-md border border-yellow-100 p-4 mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-8 gap-2">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Customer Name
              </label>
              <input
                type="text"
                name="name"
                value={tempFilters.name}
                onChange={handleFilterChange}
                placeholder="Customer name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>

            {/* Contact Number */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Contact
              </label>
              <input
                type="text"
                name="contact_number"
                value={tempFilters.contact_number}
                onChange={handleFilterChange}
                placeholder="Phone number"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>

            {/* Customer Type */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Customer Type
              </label>
              <select
                name="customer_type"
                value={tempFilters.customer_type}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">All</option>
                <option value="Individual">Individual</option>
                <option value="Business">Business</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* State */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                State
              </label>
              <input
                type="text"
                name="state"
                value={tempFilters.state}
                onChange={handleFilterChange}
                placeholder="e.g. Uttar Pradesh"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>

            {/* Interested */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Interested
              </label>
              <select
                name="isInterested"
                value={tempFilters.isInterested}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Status
              </label>
              <select
                name="status"
                value={tempFilters.status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">All</option>
                <option value="Visited">Visited</option>
                <option value="Not Visited">Not Visited</option>
                <option value="Follow Up">Follow Up</option>
              </select>
            </div>
            {/* Employee (Created By) */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Created By
              </label>
              <select
                name="created_byId"
                value={tempFilters.created_byId}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm"
              >
                <option value="">All</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Apply Filters Button */}
            <div className="flex gap-2 justify-center">
            <div className="flex justify-end mt-4">
              <button
                onClick={handleApplyFilters}
                className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-lg shadow-sm transition duration-200 flex items-center gap-2"
              >
                <svg
                  className="w-4 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => handleDownloadCSV()}
                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg shadow-sm transition duration-200 flex items-center gap-2"
              >
                <IoIosSave />
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl border border-yellow-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1000px] lg:min-w-full w-full">
              <thead>
                <tr className="bg-yellow-50 border-b-2 border-yellow-100">
                  {[
                    { key: "name", label: "Customer" },
                    { key: "contact_number", label: "Contact" },
                    { key: "address", label: "Address" },
                    { key: "isInterested", label: "Interest" },
                    { key: "status", label: "Status" },
                    { key: "location", label: "Coordinates" },
                    { key: "createdAt", label: "Created At" },
                    { key: "created_by.name", label: "Created By" },
                    { key: "enquiry_items", label: "Enquiry Items" },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() =>
                        !["address", "location", "enquiry_items"].includes(
                          col.key,
                        ) && requestSort(col.key)
                      }
                      className={`px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        !["address", "location", "enquiry_items"].includes(
                          col.key,
                        )
                          ? "cursor-pointer hover:bg-yellow-100 transition"
                          : ""
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {!["address", "location", "enquiry_items"].includes(
                          col.key,
                        ) && getSortIndicator(col.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-yellow-50">
                {data.length > 0 ? (
                  data.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-yellow-50/40 transition duration-150"
                    >
                      {/* Customer */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-800">
                            {item.name?.trim()}
                          </span>
                          <span className="text-xs text-gray-400 mt-0.5">
                            {item.customer_type}
                          </span>
                          {item.gst_number && (
                            <span className="text-xs text-gray-400">
                              GST: {item.gst_number}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Contact */}
                      <td className="px-6 py-4">
                        <a
                          href={`tel:${item.contact_number}`}
                          className="text-sm font-medium text-gray-700 hover:text-yellow-600 transition"
                        >
                          {item.contact_number}
                        </a>
                      </td>
                      {/* Address */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">
                          {item.district}, {item.village?.trim()}
                          <br />
                          <span className="text-xs text-gray-400">
                            {item.state} - {item.pincode}
                          </span>
                        </div>
                      </td>
                      {/* Interest */}
                      <td className="px-6 py-4">
                        {item.isInterested ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 shadow-sm">
                            <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1.5"></span>
                            Interested
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5"></span>
                            Not Interested
                          </span>
                        )}
                      </td>
                      {/* Status */}
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-black text-white">
                          {item.status}
                        </span>
                      </td>
                      {/* Coordinates */}
                      <td className="px-6 py-4">
                        <div className="text-xs font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded-md inline-block">
                          {formatCoordinates(item.location)}
                        </div>
                      </td>
                      {/* Created At */}
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(item.createdAt)}
                      </td>
                      {/* Created By */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                            {item.created_by?.name?.charAt(0) || "U"}
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {item.created_by?.name || "Unknown"}
                          </span>
                        </div>
                      </td>
                      {/* Enquiry Items */}
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {item.enquiry_items?.map((eq, idx) => (
                            <span
                              key={eq.id || idx}
                              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200"
                            >
                              {eq.name} ({eq.interest_in}: {eq.value || "0"})
                            </span>
                          ))}
                          {(!item.enquiry_items ||
                            item.enquiry_items.length === 0) && (
                            <span className="text-xs text-gray-400 italic">
                              No items
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <svg
                          className="w-16 h-16 text-yellow-300 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-700">
                          No matching records
                        </h3>
                        <p className="text-gray-400 mt-1">
                          Try adjusting your filters
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-yellow-50/30 border-t border-yellow-100 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className="text-sm text-gray-500">
                Showing {(appliedFilters.page - 1) * appliedFilters.limit + 1}{" "}
                to{" "}
                {Math.min(
                  appliedFilters.page * appliedFilters.limit,
                  totalRecords,
                )}{" "}
                of {totalRecords} entries
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => goToPage(appliedFilters.page - 1)}
                  disabled={appliedFilters.page === 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-yellow-200 bg-white hover:bg-yellow-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm"
                >
                  Previous
                </button>
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (appliedFilters.page <= 3) pageNum = i + 1;
                    else if (appliedFilters.page >= totalPages - 2)
                      pageNum = totalPages - 4 + i;
                    else pageNum = appliedFilters.page - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`w-9 h-9 text-sm rounded-lg transition ${
                          appliedFilters.page === pageNum
                            ? "bg-yellow-500 text-white shadow-md"
                            : "bg-white border border-yellow-200 text-gray-600 hover:bg-yellow-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => goToPage(appliedFilters.page + 1)}
                  disabled={appliedFilters.page === totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg border border-yellow-200 bg-white hover:bg-yellow-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryList;
